import React, { useState, useEffect } from 'react';

const DecisionInterface = ({ crisis, user, onBack, onDecisionMade }) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [confidence, setConfidence] = useState(50);
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Trigger entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Generate decision options based on crisis type
  const getDecisionOptions = (crisisType) => {
    const options = {
      flood: {
        A: 'Immediate evacuation of affected areas',
        B: 'Deploy emergency shelters and supplies',
        C: 'Monitor situation and prepare resources'
      },
      earthquake: {
        A: 'Deploy search and rescue teams',
        B: 'Establish emergency medical facilities',
        C: 'Coordinate with local emergency services'
      },
      conflict: {
        A: 'Negotiate humanitarian corridor',
        B: 'Establish safe zones for civilians',
        C: 'Provide remote humanitarian assistance'
      },
      default: {
        A: 'Deploy immediate emergency response',
        B: 'Coordinate with local authorities',
        C: 'Monitor and assess situation'
      }
    };

    return options[crisisType] || options.default;
  };

  const decisionOptions = getDecisionOptions(crisis.type);

  const handleSubmitDecision = async () => {
    if (!selectedOption || !rationale.trim()) {
      alert('Please select an option and provide rationale');
      return;
    }

    setSubmitting(true);

    const decisionData = {
      crisis_id: crisis.id,
      decision_text: decisionOptions[selectedOption],
      rationale: rationale,
      confidence_score: confidence,
      made_by: user.name,
      user_role: user.role,
      option_a: decisionOptions.A,
      option_b: decisionOptions.B,
      option_c: decisionOptions.C,
      selected_option: selectedOption
    };

    try {
      const response = await fetch('/api/decisions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(decisionData)
      });

      if (response.ok) {
        alert('Decision recorded successfully!');
        onDecisionMade();
      } else {
        alert('Error recording decision');
      }
    } catch (error) {
      console.error('Error submitting decision:', error);
      alert('Error recording decision');
    } finally {
      setSubmitting(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return '#28a745';
    if (confidence >= 60) return '#ffc107';
    if (confidence >= 40) return '#fd7e14';
    return '#dc3545';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 80) return 'High Confidence';
    if (confidence >= 60) return 'Moderate Confidence';
    if (confidence >= 40) return 'Low Confidence';
    return 'Very Low Confidence';
  };

  return (
    <div className={`min-h-screen bg-sand-50 transition-all duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
      {/* Header with Navigation */}
      <div className="container-grid py-8">
        <div className="flex items-center justify-between mb-12">
          <button 
            onClick={onBack}
            className="inline-flex items-center gap-3 px-0 py-2 text-ink-600 hover:text-accent-500 transition-all duration-300 group"
            disabled={submitting}
          >
            <div className="flex items-center justify-center w-8 h-8 border-2 border-ink-300 group-hover:border-accent-500 transition-all duration-300">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="transform group-hover:-translate-x-1 transition-transform duration-300">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
              </svg>
            </div>
            <span className="font-mono text-sm uppercase tracking-ultra">Back to Dashboard</span>
          </button>
          
          <div className="text-right">
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-1">Decision Interface</div>
            <h1 className="font-display text-2xl font-bold text-ink-900 wordmark">CLEAR Decision Support</h1>
          </div>
        </div>

        {/* Crisis Context Header */}
        <div className="bg-white border-2 border-ink-200 p-8 mb-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-2">Crisis Situation</div>
              <h2 className="font-display text-xl font-bold text-ink-900 mb-4">{crisis.title}</h2>
              <p className="text-ink-700 leading-relaxed mb-6">{crisis.description}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-1">Location</div>
                  <div className="font-semibold text-ink-800">{crisis.location}</div>
                </div>
                <div>
                  <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-1">Crisis Type</div>
                  <div className="font-semibold text-ink-800 capitalize">{crisis.type.replace('_', ' ')}</div>
                </div>
                <div>
                  <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-1">Severity Level</div>
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-ink-800">{crisis.severity}/5</div>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`w-2 h-2 ${i < crisis.severity ? 'bg-accent-500' : 'bg-ink-200'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-l-2 border-ink-200 pl-8">
              <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-4">Crisis Context</div>
              <div className="space-y-3">
                <div>
                  <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400 mb-1">Affected Population</div>
                  <div className="text-lg font-semibold text-ink-800">
                    {crisis.affected_population?.toLocaleString() || 'Unknown'}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400 mb-1">Crisis Started</div>
                  <div className="text-sm text-ink-700">
                    {new Date(crisis.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400 mb-1">Current Status</div>
                  <div className="inline-flex items-center px-3 py-1 bg-accent-100 border-2 border-accent-300 text-accent-800 font-mono text-tiny uppercase tracking-ultra font-bold">
                    {crisis.status}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decision Options */}
        <div className="bg-white border-2 border-ink-200 p-8 mb-8 shadow-sm">
          <div className="mb-8">
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-2">Response Options</div>
            <h3 className="font-display text-lg font-bold text-ink-900">Select Humanitarian Response</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {Object.entries(decisionOptions).map(([key, option]) => (
              <div 
                key={key}
                className={`
                  border-2 p-6 cursor-pointer transition-all duration-300 group
                  ${selectedOption === key 
                    ? 'border-accent-500 bg-accent-50 shadow-lg' 
                    : 'border-ink-300 bg-white hover:border-accent-300 hover:shadow-md'
                  }
                `}
                onClick={() => setSelectedOption(key)}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`
                    relative flex items-center justify-center w-6 h-6 border-2 transition-all duration-300
                    ${selectedOption === key 
                      ? 'border-accent-500 bg-accent-500' 
                      : 'border-ink-400 bg-white group-hover:border-accent-400'
                    }
                  `}>
                    {selectedOption === key && (
                      <div className="w-2 h-2 bg-white"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-1">
                      Option {key}
                    </div>
                    <div className={`
                      font-semibold transition-colors duration-300
                      ${selectedOption === key ? 'text-accent-700' : 'text-ink-800 group-hover:text-accent-600'}
                    `}>
                      Response Strategy
                    </div>
                  </div>
                </div>
                <p className="text-ink-700 leading-relaxed text-sm">{option}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Confidence Assessment */}
        <div className="bg-white border-2 border-ink-200 p-8 mb-8 shadow-sm">
          <div className="mb-6">
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-2">Confidence Assessment</div>
            <h3 className="font-display text-lg font-bold text-ink-900">Decision Confidence Level</h3>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="font-mono text-sm uppercase tracking-ultra text-ink-600">
                  {getConfidenceLabel(confidence)}
                </span>
                <span className="text-2xl font-bold" style={{ color: getConfidenceColor(confidence) }}>
                  {confidence}%
                </span>
              </div>
              
              <div className="relative">
                <div className="w-full h-3 bg-ink-200 relative overflow-hidden">
                  <div 
                    className="h-full transition-all duration-500 ease-out"
                    style={{ 
                      width: `${confidence}%`,
                      backgroundColor: getConfidenceColor(confidence)
                    }}
                  ></div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={confidence}
                  onChange={(e) => setConfidence(parseInt(e.target.value))}
                  className="absolute inset-0 w-full h-3 opacity-0 cursor-pointer"
                />
              </div>
              
              <div className="flex justify-between mt-2">
                <span className="font-mono text-tiny uppercase tracking-ultra text-ink-400">Low</span>
                <span className="font-mono text-tiny uppercase tracking-ultra text-ink-400">High</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decision Rationale */}
        <div className="bg-white border-2 border-ink-200 p-8 mb-8 shadow-sm">
          <div className="mb-6">
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-2">Decision Rationale</div>
            <h3 className="font-display text-lg font-bold text-ink-900">Justify Your Decision</h3>
            <p className="text-ink-600 mt-2">Provide detailed reasoning for accountability and future reference.</p>
          </div>

          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Explain your reasoning for this humanitarian response decision. Include key factors, risk assessments, and expected outcomes..."
            rows="6"
            className="w-full p-4 border-2 border-ink-300 bg-white text-ink-700 placeholder-ink-400 
                     focus:border-accent-500 focus:outline-none transition-all duration-300 
                     font-body leading-relaxed resize-none"
            required
          />
          
          <div className="mt-2 flex justify-between items-center">
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400">
              {rationale.length} characters
            </div>
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400">
              Required for decision logging
            </div>
          </div>
        </div>

        {/* Decision Maker Context */}
        <div className="bg-white border-2 border-ink-200 p-8 mb-8 shadow-sm">
          <div className="mb-6">
            <div className="font-mono text-tiny uppercase tracking-ultra text-ink-500 mb-2">Decision Authority</div>
            <h3 className="font-display text-lg font-bold text-ink-900">Decision Maker Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400 mb-2">Authorized By</div>
              <div className="text-xl font-semibold text-ink-800">{user.name}</div>
              <div className="font-mono text-sm uppercase tracking-ultra text-ink-600 mt-1">
                {user.role.replace('_', ' ')}
              </div>
            </div>
            <div>
              <div className="font-mono text-tiny uppercase tracking-ultra text-ink-400 mb-2">Decision Timestamp</div>
              <div className="text-lg text-ink-700">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <button 
            className="btn btn-ghost px-8 py-4"
            onClick={onBack}
            disabled={submitting}
          >
            <span className="font-mono text-sm uppercase tracking-ultra">Cancel Decision</span>
          </button>
          
          <button 
            className={`
              px-8 py-4 border-2 font-mono text-sm uppercase tracking-ultra transition-all duration-300
              ${(!selectedOption || !rationale.trim() || submitting)
                ? 'border-ink-300 bg-ink-100 text-ink-400 cursor-not-allowed'
                : 'border-accent-500 bg-accent-500 text-white hover:bg-accent-600 hover:border-accent-600 shadow-sm hover:shadow-lg'
              }
            `}
            onClick={handleSubmitDecision}
            disabled={submitting || !selectedOption || !rationale.trim()}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin"></div>
                Recording Decision...
              </span>
            ) : (
              'Record Humanitarian Decision'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DecisionInterface;